import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2.ts';

import {
  TestingTestApi,
  type TestingSource,
  type TestingTest,
  type TestingTestApiCreateTestingTestRequest,
  type TestingTestApiCreateTestingTestResultRequest,
  type TestingTestApiGetTestingTestRequest,
  type TestingTestApiUpdateTestingTestRequest,
  type TestingTestResult,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import { AxiosError } from 'axios';

export const useTestingStore = defineStore('testing-store', () => {
  const testingSourcesData = ref<TestingSource[]>([]);
  const currentTestingTest = ref<TestingTest | null>(null);
  const currentTestingTestResult = ref<TestingTestResult | null>(null);

  const getTestingTest = async (
    params: TestingTestApiGetTestingTestRequest,
    reassign: boolean = true,
  ) => {
    try {
      const testingTestApi = await initApi(TestingTestApi);
      const response = await testingTestApi?.getTestingTest(params);

      if (!response) return;

      if (reassign) {
        currentTestingTest.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createTestingTest = async (params: TestingTestApiCreateTestingTestRequest) => {
    try {
      const testingTestApi = await initApi(TestingTestApi);
      const response = await testingTestApi?.createTestingTest(params);

      if (!response) return;

      currentTestingTest.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateTestingTest = async (params: TestingTestApiUpdateTestingTestRequest) => {
    try {
      const testingTestApi = await initApi(TestingTestApi);
      const response = await testingTestApi?.updateTestingTest(params);

      if (!response) return;

      currentTestingTest.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createTestingTestResult = async (params: TestingTestApiCreateTestingTestResultRequest) => {
    try {
      const testingTestApi = await initApi(TestingTestApi);
      const response = await testingTestApi?.createTestingTestResult(params);

      if (!response) return;

      currentTestingTestResult.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const setCurrentTestResult = (result: TestingTestResult | null) => {
    currentTestingTestResult.value = result;
  };

  const resetTestingTest = () => {
    currentTestingTest.value = null;
    currentTestingTestResult.value = null;
  };

  return {
    createTestingTest,
    createTestingTestResult,
    currentTestingTest,
    currentTestingTestResult,
    getTestingTest,
    resetTestingTest,
    setCurrentTestResult,
    testingSourcesData,
    updateTestingTest,
  };
});
